import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Checkbox,
    CheckboxProps
} from '@mui/material';
import { toast, ToastContentProps } from 'react-toastify';
import { cancelButtonStyle, saveButtonStyle } from '../../style';
import { ReviewTimeDto } from '../../lib/dtos/ReviewDtos';
import { ChangeEventHandler, FormEventHandler, useEffect, useState } from 'react';
import { EMPTY_GUID } from '../../lib/constants/misc';
import { AxiosError } from 'axios';
import { ErrorResponse } from '../../lib/dtos/ErrorResponse';
import { StyledDialogActions } from '../../styledComponents';

export type ReviewTimeModalProps = {
    reviewTime: ReviewTimeDto | null;
    open: boolean;
    onDialogClose: () => Promise<void> | void;
    onSubmit: (formData: ReviewTimeDto) => Promise<void>;
};

const ReviewTimeModal = ({ reviewTime, open, onDialogClose, onSubmit }: ReviewTimeModalProps) => {
    const isAdd = !reviewTime?.id;

    const [minutes, setMinutes] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    useEffect(() => {
        if (!isAdd) {
            setMinutes(reviewTime.minutes.toString());
            setIsDefault(reviewTime.isDefault);
        } else {
            setMinutes('');
            setIsDefault(false);
        }
    }, [reviewTime, isAdd]);

    const handleCloseDialog = () => {
        onDialogClose.call(null);
    };

    const handleFormSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();
        const formData: ReviewTimeDto = reviewTime
            ? { ...reviewTime, isDefault, minutes: Number(minutes) }
            : { id: EMPTY_GUID, isActive: true, isDefault, minutes: Number(minutes) };

        await toast.promise(onSubmit.call(null, formData), {
            success: 'Success',
            pending: isAdd ? 'Adding value...' : 'Updating value...',
            error: {
                render: ({ data }: ToastContentProps<AxiosError<ErrorResponse>>) => {
                    return data?.response?.data.message;
                }
            }
        });
        handleCloseDialog();
    };

    const minutesChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
        setMinutes(e.target.value.replace(/[^\d]/g, '').substring(0, 3));
    };

    const isDefaultChangeHandler: CheckboxProps['onChange'] = (e, checked) => {
        setIsDefault(checked);
    };

    return (
        <Dialog open={open} onClose={handleCloseDialog}>
            <DialogTitle variant="h3">{isAdd ? 'Add' : 'Edit'} Review Time</DialogTitle>
            <form onSubmit={handleFormSubmit}>
                <DialogContent>
                    <TextField
                        required
                        size="small"
                        fullWidth
                        label="Minutes"
                        value={minutes}
                        onChange={minutesChangeHandler}
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography>
                            {reviewTime?.isDefault
                                ? 'Already standard value'
                                : 'Is this the standard value?'}
                        </Typography>
                        <Checkbox
                            checked={isDefault}
                            onChange={isDefaultChangeHandler}
                            disabled={reviewTime?.isDefault}
                        />
                    </Box>
                </DialogContent>
                <StyledDialogActions>
                    <Button sx={cancelButtonStyle} onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button sx={saveButtonStyle} type="submit">
                        Save
                    </Button>
                </StyledDialogActions>
            </form>
        </Dialog>
    );
};

export default ReviewTimeModal;
